import clsx from 'clsx'
import type React from 'react'
import { useEffect, useState } from 'react'

import { clampValue } from '@/utilities/number'

import { Icon } from '../Icon'

import './QuantitySelector.scss'

export type QuantitySelectorProps = {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  isLoading?: boolean
  isInputLocked?: boolean
}

export const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  value,
  onChange,
  min,
  max,
  isLoading,
  isInputLocked,
}) => {
  const [inputValue, setInputValue] = useState(value)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    setInputValue(event.target.valueAsNumber)
  }

  const handleBlur = () => {
    const newValue = inputValue
      ? clampValue(inputValue, { min, max })
      : min || 0
    setInputValue(newValue)
    if (newValue === value) return

    onChange(newValue)
  }

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const target = event.target as HTMLInputElement
      target.blur()
    }
  }

  const classes = [
    'quantity-selector',
    isLoading && 'quantity-selector--loading',
  ]

  return (
    <div className={clsx(classes)}>
      <span
        className={clsx([
          'quantity-selector__cta',
          min !== undefined &&
            value <= min &&
            'quantity-selector__cta--disabled',
        ])}
        onClick={() => onChange(value - 1)}
      >
        <Icon name="minus" viewBox="0 0 12 12" width={12} height={12} />
      </span>
      <input
        className="quantity-selector__input type-label"
        type="number"
        value={inputValue}
        min={min}
        max={max}
        readOnly={isInputLocked}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyUp={handleKeyUp}
      />
      <span
        className={clsx([
          'quantity-selector__cta',
          max !== undefined &&
            value >= max &&
            'quantity-selector__cta--disabled',
        ])}
        onClick={() => onChange(value + 1)}
      >
        <Icon name="plus" viewBox="0 0 12 12" width={12} height={12} />
      </span>
    </div>
  )
}
